import { useReducer, useEffect, useMemo, useCallback } from "react";
import { MeasurementReducerActions, initialState, reducer } from "./useAthleteMeasurementsApi.reducer";
import useInfoMessage from "hooks/useInfoMessage";
import MeasurementService from "services/MeasurementService";
import moment from "moment";
import AnalyticsService from "services/AnalyticsService";
import { Measurement } from "types";
import useSetLoading from "hooks/useSetLoading";
import useLogEventContext from "hooks/useLogEventContext";
import { DataVisualizationType } from "components/charting/config/chartTypes";
import { SelectedChartMeasure } from "components/charting/types";
import { useTranslation } from "react-i18next";

interface UseAthleteMeasurementsApiActions {
  getMeasurementsUpToEndDate(
    athleteId: string,
    opts: {
      endDate?: string | moment.Moment;
      measure?: SelectedChartMeasure | null;
    }
  ): Promise<Measurement[] | undefined>;
  getCompletedWorkoutMeasurements(completedWorkoutId: string): Promise<Measurement[] | undefined>;
  deleteMeasurementById(id: string): Promise<{ id: string } | undefined>;
  deleteMeasurementVideo(id: string): Promise<void>;
  updateMeasurementCompletedDate(id: string, completedDate: string): Promise<void>;
}

type UseAthleteMeasurementsApi = [Measurement[], UseAthleteMeasurementsApiActions];

type UseAthleteMeasurementsApiProps =
  | { athleteId: string; measure?: SelectedChartMeasure | null }
  | { completedWorkoutId: string };

function useAthleteMeasurementsApi(props: UseAthleteMeasurementsApiProps) {
  const pageSize = 50;
  const [measurements, dispatch] = useReducer(reducer, initialState);
  const setLoading = useSetLoading();
  const { t } = useTranslation();
  const setInfoMessage = useInfoMessage({ type: "error" });
  const logEvent = useLogEventContext();

  const getMeasurementsUpToEndDate = useCallback(
    async (aId: string, opts: { endDate: string | moment.Moment; measure?: SelectedChartMeasure | null }) => {
      if (!opts.measure?.id) return;

      try {
        setLoading(true);

        logEvent({
          [AnalyticsService.PROPERTIES.CHART_TYPE]: DataVisualizationType.MEASUREMENTS_TABLE,
          [AnalyticsService.PROPERTIES.VISUALIZATION_TYPE]: DataVisualizationType.MEASUREMENTS_TABLE,
          [AnalyticsService.PROPERTIES.CHARTING_MEASURE_ID]: opts.measure.id,
          [AnalyticsService.PROPERTIES.EXERCISE_ID]: opts.measure.type === "Exercise" ? opts.measure.id : undefined,
          [AnalyticsService.PROPERTIES.EXERCISE_CATEGORY]:
            opts.measure.type === "Exercise" ? opts.measure.metadata.categoryId : undefined,
          [AnalyticsService.PROPERTIES.CHARTING_MEASURE_TYPE]: opts.measure.type,
        });

        const result = await MeasurementService.getByAthleteId(aId, {
          exerciseId: opts.measure.id,
          size: pageSize,
          startDate: new Date(2019, 0, 1).toISOString(),
          endDate: moment(opts.endDate).subtract(1, "second").toISOString(),
        });

        dispatch({ type: MeasurementReducerActions.ADD_MEASUREMENTS, payload: result });
        setLoading(false);

        return result;
      } catch (error: any) {
        setInfoMessage({ message: error.message });
        setLoading(false);
      }
    },
    [setLoading, logEvent, setInfoMessage]
  );

  const getCompletedWorkoutMeasurements = useCallback(
    async (completedWorkoutId: string) => {
      try {
        setLoading(true);

        const result = await MeasurementService.getCompletedWorkout(completedWorkoutId);

        if (result) {
          dispatch({ type: MeasurementReducerActions.ADD_MEASUREMENTS, payload: result });
        }

        return result;
      } catch (error: any) {
        setInfoMessage({ message: error.message });
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setInfoMessage]
  );

  const isCompletedWorkoutMode = "completedWorkoutId" in props;
  const completedWorkoutId = isCompletedWorkoutMode ? props.completedWorkoutId : undefined;
  const athleteId = isCompletedWorkoutMode ? undefined : props.athleteId;
  const measure = isCompletedWorkoutMode ? undefined : props.measure;

  useEffect(() => {
    (async () => {
      if (isCompletedWorkoutMode && completedWorkoutId) {
        await getCompletedWorkoutMeasurements(completedWorkoutId);
      } else if (!isCompletedWorkoutMode && athleteId) {
        await getMeasurementsUpToEndDate(athleteId, { endDate: moment().toISOString(), measure });
      }
    })();
    return () => {
      dispatch({ type: MeasurementReducerActions.RESET });
    };
  }, [
    getMeasurementsUpToEndDate,
    getCompletedWorkoutMeasurements,
    isCompletedWorkoutMode,
    completedWorkoutId,
    athleteId,
    measure,
  ]);

  const deleteMeasurementById = useCallback(
    (measurementId: string) => {
      return (async () => {
        setLoading(true);
        try {
          const result = await MeasurementService.deleteById(measurementId);

          dispatch({ type: MeasurementReducerActions.DELETE_MEASUREMENT, payload: { id: measurementId } });

          return result;
        } catch (error: any) {
          setInfoMessage({ message: error.message });
        } finally {
          setLoading(false);
        }
      })();
    },
    [setLoading, setInfoMessage]
  );

  const deleteMeasurementVideo = useCallback(
    (measurementId: string) => {
      return (async () => {
        setLoading(true);
        try {
          await MeasurementService.deleteVideo(measurementId);

          const updatedMeasurements = measurements.map((measurement) => {
            if (measurement.id === measurementId) {
              return { ...measurement, video: null };
            }
            return measurement;
          });

          dispatch({
            type: MeasurementReducerActions.UPDATE_MEASUREMENTS,
            payload: updatedMeasurements,
          });
        } catch (error) {
          setInfoMessage({ message: t("errors.failed_to_delete_video") });
        } finally {
          setLoading(false);
        }
      })();
    },
    [setLoading, measurements, setInfoMessage, t]
  );

  const updateMeasurementCompletedDate = useCallback(
    async (measurementId: string, completedDate: string) => {
      setLoading(true);
      try {
        await MeasurementService.updateMeasurementCompletedDate(measurementId, completedDate);

        const updatedMeasurements = measurements.map((measurement) => {
          if (measurement.id === measurementId) {
            return { ...measurement, completedDate };
          }
          return measurement;
        });

        dispatch({
          type: MeasurementReducerActions.UPDATE_MEASUREMENTS,
          payload: updatedMeasurements,
        });
      } catch (error) {
        setInfoMessage({ message: t("errors.couldnt_update_completed_date") });
      } finally {
        setLoading(false);
      }
    },
    [setLoading, measurements, setInfoMessage, t]
  );

  return useMemo<UseAthleteMeasurementsApi>(
    () => [
      measurements,
      {
        deleteMeasurementById,
        getMeasurementsUpToEndDate,
        getCompletedWorkoutMeasurements,
        deleteMeasurementVideo,
        updateMeasurementCompletedDate,
      },
    ],
    [
      measurements,
      deleteMeasurementById,
      getMeasurementsUpToEndDate,
      getCompletedWorkoutMeasurements,
      deleteMeasurementVideo,
      updateMeasurementCompletedDate,
    ]
  );
}
export default useAthleteMeasurementsApi;
